import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Row, Col, Spin, Checkbox, Slider, Pagination, Typography, Button, Modal, Select, message } from "antd";
import LearnerCourseService from "../services/LearnerCourseService";
import LearnerCategoryService from "../services/LearnerCategoryService";
import LearnerApiService from "../services/LearnerApiService";
import HorizontalCourseCard from "../components/HorizontalCourseCard";
import "../styles/CourseList.css";
import { debounce } from "../utils/debounce";
import { SortAscendingOutlined, SortDescendingOutlined, DollarOutlined } from "@ant-design/icons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import LazyLoad from 'react-lazyload';
import { useAuth } from "../contexts/AuthContext";
import NotFound from "./NotFound";
import CartApiService from "../services/CartApiService";

const { Title } = Typography;

const fetchAllPaginatedData = async (serviceCall) => {
  let allItems = [];
  let currentPage = 1;
  let hasMore = true;
  const pageSize = 100; 

  while (hasMore) {
    try {
      const response = await serviceCall({ page: currentPage, pageSize });
      const items = response.courses || response.categories;
      const pagination = response.pagination;

      if (items && items.length > 0) {
        allItems = allItems.concat(items);
        const totalItems = pagination?.total;
        if (totalItems) {
          hasMore = allItems.length < totalItems;
        } else {
          hasMore = items.length === pageSize;
        }
        currentPage++;
      } else {
        hasMore = false;
      }
    } catch (error) {
      console.error("Error fetching paginated data:", error);
      hasMore = false; 
    }
  }
  return allItems;
};

const CourseList = () => {
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState(["all"]);
  const [priceRange, setPriceRange] = useState([0, 500]);
  const [tempPriceRange, setTempPriceRange] = useState([0, 500]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [sortOrder, setSortOrder] = useState("-createdAt");
  const [listLoading, setListLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { authenticated } = useAuth();
  const [showAllCategories, setShowAllCategories] = useState(false);
  const maxCategoriesToShow = 10;
  const [isInvalidQuery, setIsInvalidQuery] = useState(false);
  const [shouldResetPriceRange, setShouldResetPriceRange] = useState(false);
  const [cartCourseSlugs, setCartCourseSlugs] = useState([]);

  const sortOptions = [
    { label: "All Courses", value: "-createdAt" },
    { label: "Name (A–Z)", value: "name" },
    { label: "Name (Z–A)", value: "-name" },
    { label: "Price (Low to High)", value: "price" },
    { label: "Price (High to Low)", value: "-price" },
    { label: "Top Selling", value: "-purchaseCount" },
  ];

  useEffect(() => {
    let isList = listLoading;
    const fetchData = async () => {
      if (!isList) setLoading(true);
      try {
        let params = {
          page: currentPage,
          pageSize,
          sort: sortOrder,
          priceMin: priceRange[0],
          priceMax: priceRange[1],
        };
        if (!selectedCategories.includes("all")) {
          params.categories = selectedCategories.join(",");
        }
        const urlParams = new URLSearchParams(location.search);
        const search = urlParams.get("search");
        if (search) {
          params.search = search;
        }
        // Validate priceMin, priceMax trước khi gọi API
        if (isNaN(params.priceMin) || isNaN(params.priceMax)) {
          // Không fetch nếu giá trị không hợp lệ
          setLoading(false);
          setListLoading(false);
          return;
        }
        console.log('API params:', params);
        const [coursesResponse, categoriesResponse] = await Promise.all([
          LearnerCourseService.getLearnerCourses(params),
          LearnerCategoryService.getLearnerCategories({ limit: 100 })
        ]);
        setCourses(coursesResponse.courses);
        setTotal(coursesResponse.pagination?.totalItems || 0);
        setCategories(categoriesResponse.categories || []);
      } catch (error) {
        setError("Failed to load data. Please try again later.");
      } finally {
        if (!isList) setLoading(false);
        setListLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, [currentPage, pageSize, sortOrder, priceRange, selectedCategories, location.search]);

  useEffect(() => {
    // On mount, check for ?category=... in URL
    const params = new URLSearchParams(location.search);
    const categoryId = params.get("category");
    if (categoryId) {
      setSelectedCategories([categoryId]);
    }
    // eslint-disable-next-line
  }, []);

  const maxPrice = useMemo(
    () => Math.max(...courses.map((c) => c.price), 500),
    [courses]
  );

  const categoryCourseCounts = useMemo(() => {
    const counts = {};
    categories.forEach((cat) => {
      counts[cat._id] = 0;
    });
    courses.forEach((course) => {
      if (course.category && counts[course.category._id] !== undefined) {
        counts[course.category._id]++;
      }
    });
    return counts;
  }, [courses, categories]);

  const handleCategoryChange = (checkedValues) => {
    let newSelected;
    const isSelectingAll = checkedValues.includes("all");
    const wasAllSelected = selectedCategories.includes("all");

    if (isSelectingAll && !wasAllSelected) {
      newSelected = ["all"];
    } else if (isSelectingAll && checkedValues.length > 1) {
      newSelected = checkedValues.filter((v) => v !== "all");
    } else if (checkedValues.length === 0) {
      newSelected = ["all"];
    } else {
      newSelected = checkedValues;
    }

    setSelectedCategories(newSelected);
    setCurrentPage(1);
    setListLoading(true);
    updateQueryParams({
      categories: newSelected.includes("all") ? "" : newSelected.join(","),
      sort: sortOrder,
      priceMin: priceRange[0],
      priceMax: priceRange[1],
      page: 1,
    });
  };

  const handleSliderChange = (value) => {
    setTempPriceRange(value);
  };

  useEffect(() => {
    setTempPriceRange(priceRange);
  }, [priceRange]);

  useEffect(() => {
    const maxPriceSafe = Math.max(...courses.map((c) => c.price), 500);
    // Nếu maxPriceSafe <= 0, luôn set về [0, 500]
    if (maxPriceSafe <= 0) {
      if (priceRange[0] !== 0 || priceRange[1] !== 500) setPriceRange([0, 500]);
      if (tempPriceRange[0] !== 0 || tempPriceRange[1] !== 500) setTempPriceRange([0, 500]);
      return;
    }
    // Nếu maxPriceSafe > 0, chỉ reset nếu state không hợp lệ
    const isPriceRangeInvalid = priceRange[0] < 0 || priceRange[1] > maxPriceSafe || priceRange[1] < priceRange[0] || (priceRange[0] === 0 && priceRange[1] === 0 && maxPriceSafe > 0);
    const isTempPriceRangeInvalid = tempPriceRange[0] < 0 || tempPriceRange[1] > maxPriceSafe || tempPriceRange[1] < tempPriceRange[0] || (tempPriceRange[0] === 0 && tempPriceRange[1] === 0 && maxPriceSafe > 0);
    if (isPriceRangeInvalid) setPriceRange([0, maxPriceSafe]);
    if (isTempPriceRangeInvalid) setTempPriceRange([0, maxPriceSafe]);
  }, [courses]);

  const updateQueryParams = (params) => {
    const searchParams = new URLSearchParams(location.search);
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        searchParams.set(key, value);
      } else {
        searchParams.delete(key);
      }
    });
    navigate({ search: searchParams.toString() }, { replace: true });
  };

  const handleSortChange = (value) => {
    setSortOrder(value);
    setListLoading(true);
    updateQueryParams({
      sort: value,
      categories: selectedCategories.includes("all") ? "" : selectedCategories.join(","),
      priceMin: priceRange[0],
      priceMax: priceRange[1],
    });
  };

  useEffect(() => {
    setListLoading(true);
    updateQueryParams({
      priceMin: priceRange[0],
      priceMax: priceRange[1],
      categories: selectedCategories.includes("all") ? "" : selectedCategories.join(","),
      sort: sortOrder,
    });
    // eslint-disable-next-line
  }, [priceRange]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sort = params.get("sort");
    const categoriesParam = params.get("categories");
    const priceMin = params.get("priceMin");
    const priceMax = params.get("priceMax");
    if (sort) setSortOrder(sort);
    if (categoriesParam) {
      setSelectedCategories(categoriesParam.split(","));
    }
    if (priceMin && priceMax) {
      const min = Number(priceMin);
      const max = Number(priceMax);
      if (!isNaN(min) && !isNaN(max)) {
        setPriceRange([min, max]);
        setTempPriceRange([min, max]);
      } else {
        setPriceRange([0, 500]);
        setTempPriceRange([0, 500]);
      }
    }
    // eslint-disable-next-line
  }, []);

  const debouncedResetPrice = useCallback(
    debounce((max) => {
      setTempPriceRange([0, max]);
      setPriceRange([0, max]);
      setListLoading(true);
    }, 400),
    [setTempPriceRange, setPriceRange, setListLoading]
  );

  // Khi mount, nếu không có page trên URL, tự động thêm page=1
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.get("page")) {
      params.set("page", 1);
      navigate({ search: params.toString() }, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  // Kiểm tra URL params, nếu không hợp lệ thì render NotFound
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const page = params.get("page");
    const pageSize = params.get("pageSize");
    const priceMin = params.get("priceMin");
    const priceMax = params.get("priceMax");

    // Clamp and validate pageSize, priceMin, priceMax
    const validPageSizes = [10, 20, 50, 100];
    let clampedPageSize = validPageSizes.includes(Number(pageSize)) ? Number(pageSize) : 10;
    let minPrice = 0;
    let maxPrice = Math.max(...courses.map(c => c.price), 500);
    let clampedPriceMin = priceMin !== null && !isNaN(priceMin) ? Math.max(Number(priceMin), minPrice) : minPrice;
    let clampedPriceMax = priceMax !== null && !isNaN(priceMax) ? Math.min(Number(priceMax), maxPrice) : maxPrice;
    let shouldUpdate = false;
    if (Number(pageSize) !== clampedPageSize) {
      params.set('pageSize', clampedPageSize);
      shouldUpdate = true;
    }
    if (Number(priceMin) !== clampedPriceMin) {
      params.set('priceMin', clampedPriceMin);
      shouldUpdate = true;
    }
    if (Number(priceMax) !== clampedPriceMax) {
      params.set('priceMax', clampedPriceMax);
      shouldUpdate = true;
    }
    if (shouldUpdate) {
      navigate({ search: params.toString() }, { replace: true });
      return;
    }

    let invalid = false;
    if (
      (page && (isNaN(page) || Number(page) < 1)) ||
      (pageSize && (isNaN(pageSize) || Number(pageSize) < 1)) ||
      (priceMin && isNaN(priceMin)) ||
      (priceMax && isNaN(priceMax))
    ) {
      invalid = true;
    }
    // If page is too large (e.g., page > max page), also treat as invalid
    if (!invalid && total > 0 && page && Number(page) > Math.ceil(total / pageSize)) {
      invalid = true;
    }
    if (invalid) {
      setSortOrder("-createdAt");
      setSelectedCategories(["all"]);
      setPriceRange([0, 500]);
      setTempPriceRange([0, 500]);
      setShouldResetPriceRange(true);
      setCurrentPage(1);
      // message.warning("Invalid parameter detected, filter reset!");
      setTimeout(() => {
        navigate("/all-courses", { replace: true });
      }, 100);
      return;
    } else {
      setIsInvalidQuery(false);
    }
  }, [location.search, total, pageSize, navigate, courses]);

  useEffect(() => {
    if (shouldResetPriceRange) {
      const maxPriceSafe = Math.max(...courses.map((c) => c.price), 500);
      if (maxPriceSafe > 0) {
        setPriceRange([0, maxPriceSafe]);
        setTempPriceRange([0, maxPriceSafe]);
      } else {
        setPriceRange([0, 500]);
        setTempPriceRange([0, 500]);
      }
      setShouldResetPriceRange(false);
    }
  }, [courses, shouldResetPriceRange]);

  // Hàm refetchCart để truyền xuống HorizontalCourseCard
  const refetchCart = async () => {
    try {
      const res = await CartApiService.getCart();
      if (res.success && res.data && Array.isArray(res.data.courses)) {
        setCartCourseSlugs(slugs => {
          const newSlugs = res.data.courses.map(c => c.slug);
          console.log('Updated cartCourseSlugs:', newSlugs);
          return newSlugs;
        });
      } else {
        setCartCourseSlugs([]);
      }
    } catch (e) {
      setCartCourseSlugs([]);
    }
  };

  useEffect(() => {
    refetchCart();
    const handleCartChanged = () => refetchCart();
    window.addEventListener('cart-changed', handleCartChanged);
    return () => window.removeEventListener('cart-changed', handleCartChanged);
  }, []);

  if (isInvalidQuery) {
    return <NotFound />;
  }

  if (loading) {
    return (
      <div className="spinner-container">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <div className="error-container">{error}</div>;
  }

  return (
    <div className="course-list-container">
      <div className="course-list-main-content">
        <Row gutter={[32, 32]}>
          <Col xs={24} md={18}>
            <div className="course-list-header-row">
              <h1 className="course-list-title">All Courses</h1>
              <div className="course-list-header-actions" style={{ display: "flex", alignItems: "center" }}>
                <span style={{ marginRight: 8, fontWeight: 500 }}>Sort:</span>
                <Select
                  options={sortOptions}
                  value={sortOrder}
                  onChange={handleSortChange}
                  style={{ minWidth: 220, marginRight: 42 }}
                  dropdownMatchSelectWidth={false}
                />
              </div>
            </div>
            <div className="courses-list">
              <Spin spinning={listLoading} size="large">
                {courses.length === 0 && new URLSearchParams(location.search).get('search') ? (
                  <div style={{
                    padding: '48px 0',
                    textAlign: 'center',
                    background: '#fff',
                    borderRadius: 12,
                    margin: '32px 0',
                  }}>
                    <h2 style={{ fontWeight: 700, fontSize: 28, marginBottom: 16 }}>
                      Sorry, we couldn't find any results for <span style={{ color: '#1976d2' }}>
                        “{new URLSearchParams(location.search).get('search')}”
                      </span>
                    </h2>
                    <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 12, marginTop: 24 }}>
                      Try adjusting your search. Here are some ideas:
                    </div>
                    <ul style={{
                      display: 'inline-block',
                      textAlign: 'left',
                      fontSize: 16,
                      color: '#333',
                      lineHeight: 2,
                      margin: 0,
                      padding: 0,
                      listStyle: 'disc inside',
                    }}>
                      <li>Make sure all words are spelled correctly</li>
                      <li>Try different search terms</li>
                      <li>Try more general search terms</li>
                    </ul>
                    <div>
                      <Link
                        to="#"
                        className="not-found-home-btn"
                        style={{ marginTop: 24 }}
                        onClick={e => {
                          e.preventDefault();
                          const params = new URLSearchParams(location.search);
                          params.delete("search");
                          if (selectedCategories && !selectedCategories.includes("all")) {
                            params.set("categories", selectedCategories.join(","));
                          } else {
                            params.delete("categories");
                          }
                          navigate({ search: params.toString() }, { replace: true });
                        }}
                      >
                        Show all courses
                      </Link>
                    </div>
                  </div>
                ) : (
                  courses.map((course) => {
                    const isInCart = cartCourseSlugs.includes(course.slug);
                    // Debug log
                    console.log('course.slug:', course.slug, 'cartCourseSlugs:', cartCourseSlugs, 'isInCart:', isInCart);
                    const courseId = String(course._id || course.id);
                    return (
                      <LazyLoad height={200} offset={100} once key={courseId}>
                        <HorizontalCourseCard
                          id={courseId}
                          rawId={course.id}
                          courseSlug={course.slug}
                          image={course.imageUrl}
                          title={course.name}
                          author={course.author || "N/A"}
                          totalSections={course.sectionCount}
                          lessonCount={course.lessonCount}
                          price={course.price}
                          category={course.category?.name}
                          purchaseCount={course.purchaseCount}
                          isPurchased={!!course.bought}
                          isInCart={isInCart}
                          refetchCart={refetchCart}
                        />
                      </LazyLoad>
                    );
                  })
                )}
              </Spin>
            </div>
            <div className="pagination-container">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={total}
                onChange={(page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                  updateQueryParams({
                    page,
                    pageSize: size,
                    sort: sortOrder,
                    categories: selectedCategories.includes("all") ? "" : selectedCategories.join(","),
                    priceMin: priceRange[0],
                    priceMax: priceRange[1],
                  });
                }}
                showSizeChanger={true}
              />
            </div>
          </Col>
          <Col xs={24} md={6}>
            <div className="filter-sidebar">
              <Title level={4} className="filter-title">
                Course Category
              </Title>
              <Checkbox.Group
                className="category-checkbox-group"
                onChange={handleCategoryChange}
                value={selectedCategories}
              >
                <Row>
                  <Col span={24}>
                    <Checkbox value="all">All Courses</Checkbox>
                  </Col>
                  {(showAllCategories ? categories : categories.slice(0, maxCategoriesToShow)).map((cat) => (
                    <Col span={24} key={cat.slug}>
                      <Checkbox value={cat.slug}>
                        {cat.name}
                      </Checkbox>
                    </Col>
                  ))}
                  {categories.length > maxCategoriesToShow && (
                    <Col span={24} style={{ textAlign: 'center', marginTop: 8 }}>
                      <Button
                        type="link"
                        onClick={() => setShowAllCategories((prev) => !prev)}
                        style={{ padding: 0 }}
                      >
                        {showAllCategories ? (
                          <>
                            <span style={{ color: '#1890ff', marginRight: 4 }}>&#9650;</span> Show less
                          </>
                        ) : (
                          <>
                            <span style={{ color: '#1890ff', marginRight: 4 }}>&#9660;</span> Show more
                          </>
                        )}
                      </Button>
                    </Col>
                  )}
                </Row>
              </Checkbox.Group>
              <div style={{ marginTop: 32 }}>
                <Title level={5} className="price-filter-title">Price Range</Title>
                {(() => {
                  const safeTempPriceRange =
                    Array.isArray(tempPriceRange) &&
                    tempPriceRange.length === 2 &&
                    !isNaN(tempPriceRange[0]) &&
                    !isNaN(tempPriceRange[1]) &&
                    tempPriceRange[0] <= tempPriceRange[1]
                      ? tempPriceRange
                      : [0, maxPrice];
                  return (
                    <Slider
                      range
                      min={0}
                      max={maxPrice}
                      value={safeTempPriceRange}
                      onChange={handleSliderChange}
                      tipFormatter={value => (value != null && value !== undefined && !isNaN(value)) ? `${value.toLocaleString()}$` : ''}
                    />
                  );
                })()}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                  <span>{priceRange[0] != null && !isNaN(priceRange[0]) ? priceRange[0].toLocaleString() + '$' : ''}</span>
                  <span>{priceRange[1] != null && !isNaN(priceRange[1]) ? priceRange[1].toLocaleString() + '$' : ''}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                  <Button
                    type="primary"
                    onClick={() => {
                      setCurrentPage(1);
                      setPriceRange(tempPriceRange);
                      setListLoading(true);
                    }}
                    style={{ width: '48%' }}
                    disabled={tempPriceRange[0] === priceRange[0] && tempPriceRange[1] === priceRange[1]}
                  >
                    Filter
                  </Button>
                  <Button onClick={() => debouncedResetPrice(maxPrice)} style={{ width: '48%' }}>Reset</Button>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default CourseList;
